import { storableError } from '../../util/errors';
import {
  startIdentityVerificationChallenge,
  submitIdentityVerificationCapture,
} from '../../util/api';

// ================ Action types ================ //

export const START_CHALLENGE_REQUEST = 'app/AIInterviewIdentityVerificationPage/START_CHALLENGE_REQUEST';
export const START_CHALLENGE_SUCCESS = 'app/AIInterviewIdentityVerificationPage/START_CHALLENGE_SUCCESS';
export const START_CHALLENGE_ERROR = 'app/AIInterviewIdentityVerificationPage/START_CHALLENGE_ERROR';
export const SUBMIT_CAPTURE_REQUEST = 'app/AIInterviewIdentityVerificationPage/SUBMIT_CAPTURE_REQUEST';
export const SUBMIT_CAPTURE_SUCCESS = 'app/AIInterviewIdentityVerificationPage/SUBMIT_CAPTURE_SUCCESS';
export const SUBMIT_CAPTURE_ERROR = 'app/AIInterviewIdentityVerificationPage/SUBMIT_CAPTURE_ERROR';

// ================ Reducer ================ //

const initialState = {
  inProgress: false,
  error: null,
  challengeSequence: null,
  mode: null,
  outcome: null, // 'pass' | 'fail' | 'locked' | null
  locked: false,
  // All three arrive from the server already; the reducer used to drop them.
  // `attemptNumber` is the attempt in play ("Attempt N of `maxAttempts`"); `stepValues` is the
  // parallel array of server-issued digits, non-null only at the `enterNumber` index, which the
  // candidate has to read off the screen in order to type it back.
  attemptNumber: null,
  maxAttempts: null,
  stepValues: null,
  // The retry discriminator. `outcome` CANNOT carry this: a second consecutive failure writes
  // `'fail'` over `'fail'` and the post-increment `attemptCount` equals what the challenge start
  // already stored, so nothing in the state shape changes and any equality-based branch or effect
  // dep is blind to it — which is exactly how attempt 3 became unreachable. This flag works because
  // the reducer runs on every dispatched action whether or not the value changed: the retry state is
  // entered by `SUBMIT_CAPTURE_SUCCESS` and left by `START_CHALLENGE_REQUEST`, both of which are
  // dispatched every time regardless of state.
  awaitingRetry: false,
};

export default function aiInterviewIdentityVerificationPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case START_CHALLENGE_REQUEST:
      // Leaving the retry state. Cleared here rather than on SUCCESS so the "Try again" click
      // tears the failed state down immediately instead of leaving it on screen for the round trip.
      return { ...state, inProgress: true, error: null, awaitingRetry: false };
    case START_CHALLENGE_SUCCESS:
      return {
        ...state,
        inProgress: false,
        challengeSequence: payload.challengeSequence || null,
        mode: payload.mode || null,
        // Kept alongside `challengeSequence` because they are issued with it, in the same service
        // call, and are meaningless apart from it (`identityVerificationService.js:214-221`).
        attemptNumber: payload.attemptNumber || null,
        maxAttempts: payload.maxAttempts || null,
        stepValues: payload.stepValues || null,
        // A challenge start against an already-locked check surfaces as locked.
        locked: !!payload.locked,
        // `alreadyPassed` is a terminal PASS, not a missing challenge. The service returns
        // `{ alreadyPassed: true, ... }` with NO `challengeSequence` when this check has already
        // passed identity (`identityVerificationService.js:76`), which is reachable in production
        // whenever a candidate reloads the gate page or re-enters after a disconnect. Without
        // mapping it to an outcome the screen sits on "Preparing your verification…" forever and
        // never fires `onPassed`, dead-ending a candidate who has ALREADY satisfied the gate.
        // `submitCapture` already normalizes the same state to `outcome: 'pass'`.
        outcome: payload.alreadyPassed ? 'pass' : payload.locked ? 'locked' : state.outcome,
      };
    case START_CHALLENGE_ERROR:
      return {
        ...state,
        inProgress: false,
        error: payload,
        locked: payload && payload.code === 'IDENTITY_LOCKED' ? true : state.locked,
        outcome: payload && payload.code === 'IDENTITY_LOCKED' ? 'locked' : state.outcome,
      };
    case SUBMIT_CAPTURE_REQUEST:
      return { ...state, inProgress: true, error: null };
    case SUBMIT_CAPTURE_SUCCESS:
      return {
        ...state,
        inProgress: false,
        outcome: payload.outcome,
        locked: payload.outcome === 'locked',
        // Entering the retry state. A lockout is terminal and a pass leaves the screen, so only a
        // plain failure is retryable.
        awaitingRetry: payload.outcome === 'fail',
        // The service reports the POST-increment count on fail/lock (`:344`, `:359`, `:362`), which
        // is the attempt just spent — exactly what the retry render has to name. A pass carries no
        // `attemptCount` (`:353`), so hold whatever the challenge start recorded rather than
        // blanking the field.
        attemptNumber:
          payload.attemptCount === undefined || payload.attemptCount === null
            ? state.attemptNumber
            : payload.attemptCount,
      };
    case SUBMIT_CAPTURE_ERROR:
      return {
        ...state,
        inProgress: false,
        error: payload,
        locked: payload && payload.code === 'IDENTITY_LOCKED' ? true : state.locked,
        outcome: payload && payload.code === 'IDENTITY_LOCKED' ? 'locked' : state.outcome,
      };
    default:
      return state;
  }
}

// ================ Thunks ================ //

// `interviewToken` is the token from the `/skillfit-interview/:token` URL. It is NOT what proves
// ownership — the server independently re-derives the set of tokens belonging to the authenticated
// candidate and honours this one only if it is a member (see
// `server/ai-interview/routes/candidateTokenResolver.js`). Sending it is what lets a candidate who
// holds more than one invitation tell the server WHICH interview this request is for.
export const startChallenge = ({ interviewToken } = {}) => dispatch => {
  dispatch({ type: START_CHALLENGE_REQUEST });
  return startIdentityVerificationChallenge({ interviewToken })
    .then(response => {
      dispatch({ type: START_CHALLENGE_SUCCESS, payload: response });
      return response;
    })
    .catch(e => {
      dispatch({ type: START_CHALLENGE_ERROR, error: true, payload: storableError(e) });
      throw e;
    });
};

// Motion is evaluated in the browser, so the capture payload carries NO frames. It carries
// the single reference still (`referenceImage`, the only thing face comparison needs), the per-step
// assertions the client already evaluated (`stepResults`, recorded as non-authoritative evidence)
// and the digits the candidate typed (`enteredValues`, the only thing the server actually scores).
// `stepValues` is deliberately absent: the server reads the answer from its own persisted attempt
// document, never from the body, so the client cannot supply the answer it is graded against
// (`identityVerification.routes.js:81-86`). Sending it would be handing over the answer key.
// `neutralHeadPose` is the one addition: nine rotation-matrix values, or absent. It is
// named here rather than spread in for the same reason the receiving route names it — and it is a
// rotation term, not imagery, so the reference still stays the only image this payload carries.
export const submitCapture = ({
  referenceImage,
  stepResults,
  enteredValues,
  neutralHeadPose,
  interviewToken,
} = {}) => dispatch => {
  dispatch({ type: SUBMIT_CAPTURE_REQUEST });
  return submitIdentityVerificationCapture({
    referenceImage,
    stepResults,
    enteredValues,
    neutralHeadPose,
    interviewToken,
  })
    .then(response => {
      dispatch({ type: SUBMIT_CAPTURE_SUCCESS, payload: response });
      return response;
    })
    .catch(e => {
      dispatch({ type: SUBMIT_CAPTURE_ERROR, error: true, payload: storableError(e) });
      throw e;
    });
};
